import type { CurrentUser, SessionResult } from './generated/types';
import { startClientPerformancePhase } from './requestPerformance';
import {
  ActiveApiError,
  type ActiveTransport,
  type RecoveredSession,
  weComEligibilityProblem,
} from './transport';

export type SessionPause = {
  message: string;
  retryable: boolean;
  kind: 'connection' | 'authentication' | 'authorization' | 'eligibility';
} | null;
export const renewableSessionCodes = new Set([
  'AUTHENTICATION_REQUIRED',
  'SESSION_EXPIRED',
]);

export function sameAuthorization(left: CurrentUser, right: CurrentUser) {
  return (
    left.id === right.id &&
    left.companyId === right.companyId &&
    right.status === 'active' &&
    left.permissionVersion === right.permissionVersion &&
    left.roleViewEnabled === right.roleViewEnabled &&
    left.primaryDepartmentId === right.primaryDepartmentId &&
    JSON.stringify(left.permissions) === JSON.stringify(right.permissions) &&
    JSON.stringify(left.dataScopes) === JSON.stringify(right.dataScopes) &&
    JSON.stringify(left.roles) === JSON.stringify(right.roles) &&
    JSON.stringify(left.roleView) === JSON.stringify(right.roleView) &&
    JSON.stringify(left.permissionDataScopes) ===
      JSON.stringify(right.permissionDataScopes) &&
    JSON.stringify(left.departments) === JSON.stringify(right.departments)
  );
}

/** One session rotation shared by quote requests and the existing refresh control.
 * Only the failed HTTP request is replayed, with its original body/CAS/key.
 * No draft data or credentials are persisted here.
 */
export function createQuoteSessionRecovery(options: {
  transport: ActiveTransport;
  current(): {
    user: CurrentUser;
    csrfToken: string | null;
    credentialChanged?: boolean;
  } | null;
  accept(session: RecoveredSession): void;
  pause(value: SessionPause): void;
  onSessionEnded?(owner: Pick<CurrentUser, 'id' | 'companyId'>): void;
  /** Local-development escape hatch only. Formal runtimes must omit it. */
  reestablish?: () => Promise<SessionResult>;
}) {
  let generation = 0;
  let flight: Promise<void> | null = null;
  let blocked: SessionPause = null;
  const assertUnblocked = () => {
    if (blocked)
      throw new ActiveApiError(
        blocked.kind === 'connection' ? null : 401,
        blocked.message,
      );
  };
  const fail = (
    message: string,
    retryable = false,
    kind: NonNullable<SessionPause>['kind'] = 'authentication',
  ) => {
    blocked = { message, retryable, kind };
    options.pause(blocked);
    return new ActiveApiError(kind === 'connection' ? null : 401, message);
  };
  const acceptSameAuthorization = (
    before: { user: CurrentUser },
    session: RecoveredSession,
  ) => {
    const current = options.current();
    if (
      !current ||
      !sameAuthorization(before.user, current.user) ||
      !sameAuthorization(before.user, session.user)
    )
      throw fail(
        '身份或授权范围已变化，原编辑已隔离；未续接保存。请联系管理员处理，勿刷新页面。',
        false,
        'authorization',
      );
    options.accept(session);
    generation += 1;
    blocked = null;
    options.pause(null);
  };
  const refresh = (afterExternalLogin = false): Promise<void> => {
    if (flight) return flight;
    if (blocked && !blocked.retryable && !afterExternalLogin)
      return Promise.reject(new ActiveApiError(401, blocked.message));
    const before = options.current();
    if (!before)
      return Promise.reject(new ActiveApiError(401, '没有可续期会话'));
    // Normal renewal is transparent; only failed recovery changes visible UI.

    flight = Promise.resolve().then(async () => {
      const finishRefresh = startClientPerformancePhase('session', 'refresh');
      try {
        if (!before.csrfToken)
          throw new ActiveApiError(403, '缺少 CSRF，无法安全续期。');
        const session = await options.transport.refreshSession(
          before.csrfToken,
        );
        acceptSameAuthorization(before, session);
      } catch (error) {
        const eligibility =
          error instanceof ActiveApiError
            ? weComEligibilityProblem(error.status, error.code, error.title)
            : null;
        if (eligibility?.kind === 'denied') {
          if (error instanceof ActiveApiError && error.status === 401)
            options.onSessionEnded?.(before.user);
          throw fail(eligibility.message, false, 'eligibility');
        }
        if (
          !eligibility &&
          (!blocked || blocked.retryable) &&
          options.reestablish
        ) {
          try {
            acceptSameAuthorization(before, await options.reestablish());
            return;
          } catch (reestablishError) {
            if (blocked && !blocked.retryable) throw reestablishError;
          }
        }
        if (!blocked || blocked.retryable) {
          const transient =
            !(error instanceof ActiveApiError) ||
            error.status === null ||
            (error.status !== null && error.status >= 500);
          if (error instanceof ActiveApiError && error.status === 401)
            options.onSessionEnded?.(before.user);
          fail(
            eligibility?.message ??
              (transient
                ? '连接暂时异常，编辑保留在本页；恢复连接后重试，尚未继续服务器操作。'
                : '登录已失效，编辑保留在本页；请重新登录，勿刷新或退出。'),
            eligibility?.kind === 'unknown' || (!eligibility && transient),
            eligibility?.kind === 'unknown' || (!eligibility && transient)
              ? 'connection'
              : 'authentication',
          );
        }
        throw error;
      } finally {
        finishRefresh();
        flight = null;
      }
    });
    return flight;
  };
  const waitForFlight = async (phase: 'flight-before' | 'flight-after') => {
    const pending = flight;
    if (!pending) return;
    const finish = startClientPerformancePhase('session', phase);
    try {
      await pending;
    } finally {
      finish();
    }
  };
  const revalidateCredential = async (
    phase: 'credential-before' | 'credential-after',
  ) => {
    const finish = startClientPerformancePhase('session', phase);
    try {
      await refresh();
    } finally {
      finish();
    }
  };
  const requestResponse: ActiveTransport['requestResponse'] = async (
    path,
    init = {},
  ) => {
    if (flight) await waitForFlight('flight-before');
    assertUnblocked();
    const before = options.current();
    if (!before)
      throw new ActiveApiError(401, '没有有效会话，未发送报价请求。');
    // Cookie rotation can belong to another login as well as another tab's
    // ordinary renewal. Verify the mounted owner before dispatching any command.
    if (before.credentialChanged)
      await revalidateCredential('credential-before');
    const started = generation;
    const send = () => {
      const current = options.current();
      if (blocked || !current || !sameAuthorization(before.user, current.user))
        throw new ActiveApiError(401, '会话已暂停，未继续报价请求。');
      // 异步恢复后共享 cookie 仍可能再次变化，不能采用尚未核验的新凭据发命令。
      if (current.credentialChanged)
        throw fail(
          '登录正在由其他页面更新，编辑保留在本页；请重试连接。',
          true,
          'connection',
        );
      const headers = new Headers(init.headers);
      if (headers.has('X-CSRF-Token') && current.csrfToken)
        headers.set('X-CSRF-Token', current.csrfToken);
      return options.transport.requestResponse(path, {
        ...init,
        headers: Object.fromEntries(headers),
      });
    };
    let response = await send();
    if (flight) await waitForFlight('flight-after');
    // A second old request must not silently start another rotation after the
    // first failed. Only the explicit recovery control may retry that failure.
    assertUnblocked();
    if (response.status === 401) {
      const problem = await response
        .clone()
        .json()
        .catch(() => ({}));
      const eligibility = weComEligibilityProblem(
        response.status,
        problem?.code ?? problem?.errorCode,
        problem?.title,
      );
      if (eligibility?.kind === 'denied') {
        options.onSessionEnded?.(before.user);
        throw fail(eligibility.message, false, 'eligibility');
      }
      if (renewableSessionCodes.has(problem?.code ?? problem?.errorCode)) {
        // A late old-credential response reuses the already rotated session.
        if (started === generation) await refresh();
        if (init.signal?.aborted)
          throw new DOMException('Aborted', 'AbortError');
        response = await send();
      }
      if (response.status === 401) {
        options.onSessionEnded?.(before.user);
        throw fail(
          '会话仍无效，原编辑仅在本页内存中隔离保留；未继续保存或离开。',
        );
      }
    }
    if (response.status === 503) {
      const problem = await response
        .clone()
        .json()
        .catch(() => ({}));
      const eligibility = weComEligibilityProblem(
        response.status,
        problem?.code ?? problem?.errorCode,
        problem?.title,
      );
      if (eligibility?.kind === 'unknown')
        throw new ActiveApiError(
          response.status,
          eligibility.message,
          'DEPENDENCY_UNAVAILABLE',
          problem?.title,
        );
    }
    // 请求在途时另一页也可能换身份；核验实际共享会话后才允许应用原响应。
    if (options.current()?.credentialChanged)
      await revalidateCredential('credential-after');
    const current = options.current();
    if (current?.credentialChanged)
      throw fail(
        '登录正在由其他页面更新，编辑保留在本页；请重试连接。',
        true,
        'connection',
      );
    if (blocked || !current || !sameAuthorization(before.user, current.user))
      throw new ActiveApiError(401, '会话已暂停，未应用报价响应。');
    return response;
  };
  return {
    refresh: () => refresh(),
    // A separate WeCom window may replace the shared cookie after an
    // authentication pause. Revalidate its actual owner before unblocking the
    // retained editor; the ordinary retry must remain closed for terminal 401s.
    recheckAfterExternalLogin: async () => {
      // A request that started before the popup rotated the cookie cannot
      // establish ownership of the new credential, even if it succeeds.
      if (flight) await flight.catch(() => undefined);
      await refresh(true);
    },
    transport: { ...options.transport, requestResponse },
  };
}
